import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetHoldingDetailsQuery,
  useGetHoldingDiversificationQuery,
  useGetHoldingDiversificationDetailsQuery,
  useGetAlsoHeldHoldingsQuery,
  HoldingViewModel,
} from '@/store/holdingsApi';
import { useGetTabRecordsQuery } from '@/store/tabsApi';
import { UseHoldingDataReturn } from '@/components/flyouts/types';
import { PortfolioDiversificationIdNumber } from '@/utils/enums';

interface UseHoldingDataParams {
  holding: HoldingViewModel | null;
  endDate?: string;
  /** Advisor-context investor scope. The backend requires this to resolve
   * diversification data for the Advisor role; omit it for the end-user flyout. */
  investorId?: number;
}

export const useHoldingData = ({
  holding,
  endDate,
  investorId,
}: UseHoldingDataParams): UseHoldingDataReturn => {
  // Skip all queries if no holding ID
  const shouldSkip = !holding?.HoldingId;

  // Call tabs API early to match legacy behavior
  const tabsQuery = useGetTabRecordsQuery('investor-holding', {
    skip: shouldSkip,
  });

  const holdingDetailsQuery = useGetHoldingDetailsQuery(
    shouldSkip
      ? skipToken
      : {
          holdingId: holding.HoldingId,
          asOfDate: endDate,
          startDate: '',
          endDate: '',
          searchTerm: '',
          page: 1,
          sort: [{ Property: 'Balance', Direction: 'desc', Order: 0 }],
        },
    { skip: shouldSkip },
  );

  const diversificationQuery = useGetHoldingDiversificationQuery(
    shouldSkip
      ? skipToken
      : {
          holdingId: holding.HoldingId,
          asOfDate: endDate,
          diversificationId: PortfolioDiversificationIdNumber.ROOT,
          categoryId: 0,
          ...(investorId !== undefined ? { investorId } : {}),
        },
    { skip: shouldSkip },
  );

  const diversificationDetailsQuery = useGetHoldingDiversificationDetailsQuery(
    shouldSkip
      ? skipToken
      : {
          holdingId: holding.HoldingId,
          asOfDate: endDate,
          diversificationId: PortfolioDiversificationIdNumber.ROOT,
          searchTerm: '',
          page: 1,
          sort: [{ Property: 'Balance', Direction: 'desc', Order: 0 }],
          ...(investorId !== undefined ? { investorId } : {}),
        },
    { skip: shouldSkip },
  );

  const alsoHeldHoldingsQuery = useGetAlsoHeldHoldingsQuery(
    shouldSkip
      ? skipToken
      : {
          alsoHeldHoldingId: holding.HoldingId,
          asOfDate: endDate,
          page: 1,
          sort: [{ Property: 'Balance', Direction: 'desc', Order: 0 }],
        },
    { skip: shouldSkip },
  );

  // The flyout stays mounted across holding selections, so these hook instances
  // persist. RTK Query's `data` is the last-known-good result across *argument*
  // changes and `isLoading` is false whenever that stale `data` exists, which
  // rendered the previous holding's details under the new holding's header until
  // the new responses landed. Read `currentData` (arg-specific; undefined while a
  // new arg is in flight) and treat that in-flight gap as loading.
  const isLoading = useMemo(
    () =>
      tabsQuery.isLoading ||
      holdingDetailsQuery.isLoading ||
      (holdingDetailsQuery.isFetching &&
        holdingDetailsQuery.currentData === undefined) ||
      diversificationQuery.isLoading ||
      (diversificationQuery.isFetching &&
        diversificationQuery.currentData === undefined) ||
      diversificationDetailsQuery.isLoading ||
      (diversificationDetailsQuery.isFetching &&
        diversificationDetailsQuery.currentData === undefined) ||
      alsoHeldHoldingsQuery.isLoading ||
      (alsoHeldHoldingsQuery.isFetching &&
        alsoHeldHoldingsQuery.currentData === undefined),
    [
      tabsQuery.isLoading,
      holdingDetailsQuery.isLoading,
      holdingDetailsQuery.isFetching,
      holdingDetailsQuery.currentData,
      diversificationQuery.isLoading,
      diversificationQuery.isFetching,
      diversificationQuery.currentData,
      diversificationDetailsQuery.isLoading,
      diversificationDetailsQuery.isFetching,
      diversificationDetailsQuery.currentData,
      alsoHeldHoldingsQuery.isLoading,
      alsoHeldHoldingsQuery.isFetching,
      alsoHeldHoldingsQuery.currentData,
    ],
  );

  const hasError = useMemo(
    () =>
      !!tabsQuery.error ||
      !!holdingDetailsQuery.error ||
      !!diversificationQuery.error ||
      !!diversificationDetailsQuery.error ||
      !!alsoHeldHoldingsQuery.error,
    [
      tabsQuery.error,
      holdingDetailsQuery.error,
      diversificationQuery.error,
      diversificationDetailsQuery.error,
      alsoHeldHoldingsQuery.error,
    ],
  );

  const errors = useMemo(
    () => ({
      tabsError: tabsQuery.error,
      holdingDetailsError: holdingDetailsQuery.error,
      diversificationError: diversificationQuery.error,
      diversificationDetailsError: diversificationDetailsQuery.error,
      alsoHeldHoldingsError: alsoHeldHoldingsQuery.error,
    }),
    [
      tabsQuery.error,
      holdingDetailsQuery.error,
      diversificationQuery.error,
      diversificationDetailsQuery.error,
      alsoHeldHoldingsQuery.error,
    ],
  );

  return {
    tabRecords: tabsQuery.data,
    holdingDetails: holdingDetailsQuery.currentData,
    diversificationData: diversificationQuery.currentData,
    diversificationDetailsData: diversificationDetailsQuery.currentData,
    alsoHeldHoldingsData: alsoHeldHoldingsQuery.currentData,
    isLoading,
    hasError,
    errors,
  };
};
